import React, { useEffect, useState } from "react";
import api from "../utils/api";

const SYMBOL = { usd: "$", inr: "₹" };

const formatAmount = (amount, currency) =>
  amount == null ? "" : `${SYMBOL[currency] || ""}${(amount / 100).toFixed(2).replace(/\.00$/, "")}`;

const formatDate = (epochSeconds) =>
  epochSeconds ? new Date(epochSeconds * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

/**
 * Account → Subscriptions. Lists the signed-in user's auto-renewing payments (paid plan,
 * monthly/yearly contributions, sponsorships) and opens Stripe's Customer Portal, where
 * they can cancel online at any time.
 */
export default function SubscriptionPanel() {
  const [subs, setSubs] = useState(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/api/payments/subscriptions")
      .then((res) => setSubs(res.data?.subscriptions || []))
      .catch(() => setSubs([]));
  }, []);

  const openPortal = async () => {
    setError("");
    setOpening(true);
    try {
      const res = await api.post("/api/payments/billing-portal");
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError("Could not open the subscription manager. Please try again.");
    } catch (err) {
      setError(err.response?.data?.error || "Could not open the subscription manager. Please try again.");
    }
    setOpening(false);
  };

  if (subs === null) return null;

  return (
    <section className="acct-subscriptions" id="subscriptions" aria-labelledby="acct-subs-heading">
      <h2 id="acct-subs-heading">Subscriptions</h2>
      {subs.length === 0 ? (
        <p className="acct-subs-empty">You have no recurring payments.</p>
      ) : (
        <ul className="acct-subs-list">
          {subs.map((s) => (
            <li key={s.id} className="acct-subs-item">
              <strong>{s.description}</strong>{" "}
              {s.amount != null && (
                <span>
                  {formatAmount(s.amount, s.currency)} / {s.interval === "year" ? "year" : "month"}
                </span>
              )}
              <div className="acct-subs-meta">
                {s.cancelAtPeriodEnd
                  ? `Cancelled: ends on ${formatDate(s.currentPeriodEnd)}, no further charges.`
                  : s.status === "past_due" || s.status === "unpaid"
                    ? "Payment problem: please update your card."
                    : `Renews automatically on ${formatDate(s.currentPeriodEnd)}.`}
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="acct-error" role="alert">{error}</p>}
      {subs.length > 0 && (
        <>
          <button type="button" className="acct-profile-link" onClick={openPortal} disabled={opening}>
            {opening ? "Opening…" : "Manage or cancel subscription"}
          </button>
          <p className="acct-subs-note">
            Opens our secure payment partner, Stripe, where you can cancel, change your card, or download receipts.
            Cancelling stops future charges; access continues until the end of the period you've paid for.
          </p>
        </>
      )}
    </section>
  );
}
